import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/i18n";

const Section = ({ title, children }) => (
  <div className="mt-10">
    <h2 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight text-[#0F2645]">{title}</h2>
    <div className="mt-4 text-sm sm:text-base leading-relaxed text-[#5C6A87] space-y-3">{children}</div>
  </div>
);

const content = {
  fr: {
    title: "Conditions générales d'utilisation",
    updated: "Dernière mise à jour : juin 2026",
    sections: [
      {
        title: "1. Objet",
        body: [
          "Devisto Studio est un logiciel en ligne (SaaS) destiné aux artisans et entreprises du bâtiment en Belgique. Il permet de constituer un dossier de chantier sur place, de préparer un devis, de planifier et documenter les interventions, puis d'établir les factures d'acompte, les factures finales et les notes de crédit.",
          "Devisto n'est pas un intermédiaire commercial : il ne met pas en relation des clients avec des entrepreneurs et ne prend aucune commission sur vos chantiers. Vous restez seul titulaire de la relation avec vos clients et seul responsable de l'exécution de vos travaux, de vos garanties, de vos assurances et de la conformité de vos installations.",
        ],
      },
      {
        title: "2. Abonnement et essai",
        body: [
          "L'inscription ouvre un essai gratuit de 30 jours, sans carte bancaire. À l'issue de l'essai, l'accès est soumis à un abonnement de 25 € par mois et par société, tous les collaborateurs de la société inclus, sans limite de devis, de dossiers ou de factures.",
          "L'abonnement est mensuel, sans durée d'engagement, et résiliable à tout moment depuis l'espace Abonnement. Il reste actif jusqu'au terme de la période déjà payée. Les paiements sont traités par Stripe ; la TVA applicable est calculée automatiquement.",
        ],
      },
      {
        title: "3. Vos documents et vos données",
        body: [
          "Les dossiers, photos, devis, rapports d'intervention, factures et fiches clients que vous créez vous appartiennent. Devisto ne les exploite à aucune fin commerciale, ne les revend pas et ne les communique à aucun tiers.",
          "Les devis et factures sont établis sous votre seule responsabilité : vous en validez le contenu, les prix, les taux de TVA et les mentions légales avant tout envoi. Devisto fournit l'outil, pas un conseil fiscal ou comptable.",
          "Vous pouvez exporter vos données à tout moment (PDF, CSV, UBL). En cas de résiliation, vos données restent récupérables pendant 30 jours, puis sont supprimées.",
        ],
      },
      {
        title: "4. Assistance par intelligence artificielle",
        body: [
          "Certaines fonctions (dictée sur chantier, lecture d'une plaque signalétique, proposition de lignes de devis, relecture d'un devis avant envoi) reposent sur des modèles d'intelligence artificielle. Leurs résultats sont des propositions : elles doivent être vérifiées et validées par vous avant d'être envoyées à un client.",
          "Devisto ne peut être tenu responsable d'une erreur de quantité, de prix, de taux de TVA ou de description issue d'une suggestion automatique que vous auriez validée.",
        ],
      },
      {
        title: "5. Disponibilité et responsabilité",
        body: [
          "Devisto met en œuvre les moyens raisonnables pour assurer la disponibilité du service. Le mode hors ligne permet de continuer à travailler sans réseau sur chantier : les données sont synchronisées dès le retour de la connexion.",
          "La responsabilité de Devisto est en tout état de cause limitée au montant des sommes effectivement payées au titre de l'abonnement sur les douze derniers mois.",
        ],
      },
      {
        title: "6. Usage du service",
        body: [
          "Chaque société dispose d'un compte responsable et peut inviter ses ouvriers. Les identifiants sont personnels et ne peuvent être partagés entre plusieurs sociétés. Vous vous engagez à fournir des informations exactes et à ne pas utiliser le service à des fins frauduleuses.",
        ],
      },
      {
        title: "7. Droit applicable",
        body: ["Les présentes conditions sont soumises au droit belge. Tout litige relève des tribunaux compétents de Belgique."],
      },
    ],
  },
  nl: {
    title: "Algemene gebruiksvoorwaarden",
    updated: "Laatste update: juni 2026",
    sections: [
      {
        title: "1. Voorwerp",
        body: [
          "Devisto Studio is online software (SaaS) voor vakmensen en bouwbedrijven in België. U stelt een werfdossier samen ter plaatse, bereidt een bestek voor, plant en documenteert interventies en maakt vervolgens voorschotfacturen, eindfacturen en creditnota's op.",
          "Devisto is geen commerciële tussenpersoon: het brengt geen klanten en aannemers samen en neemt geen commissie op uw werven. U blijft de enige houder van de relatie met uw klanten en alleen verantwoordelijk voor de uitvoering van uw werken, uw garanties, uw verzekeringen en de conformiteit van uw installaties.",
        ],
      },
      {
        title: "2. Abonnement en proefperiode",
        body: [
          "De inschrijving opent een gratis proefperiode van 30 dagen, zonder bankkaart. Daarna is de toegang onderworpen aan een abonnement van € 25 per maand en per vennootschap, alle medewerkers inbegrepen, zonder limiet op bestekken, dossiers of facturen.",
          "Het abonnement is maandelijks, zonder verbintenisduur, en op elk moment opzegbaar via de rubriek Abonnement. Het blijft actief tot het einde van de reeds betaalde periode. Betalingen worden verwerkt door Stripe; de toepasselijke btw wordt automatisch berekend.",
        ],
      },
      {
        title: "3. Uw documenten en uw gegevens",
        body: [
          "De dossiers, foto's, bestekken, interventierapporten, facturen en klantfiches die u aanmaakt, zijn uw eigendom. Devisto gebruikt ze voor geen enkel commercieel doel, verkoopt ze niet en deelt ze met geen enkele derde.",
          "Bestekken en facturen worden onder uw uitsluitende verantwoordelijkheid opgesteld: u valideert de inhoud, de prijzen, de btw-tarieven en de wettelijke vermeldingen vóór elke verzending. Devisto levert het gereedschap, geen fiscaal of boekhoudkundig advies.",
          "U kunt uw gegevens op elk moment exporteren (PDF, CSV, UBL). Bij opzegging blijven uw gegevens 30 dagen recupereerbaar en worden ze daarna verwijderd.",
        ],
      },
      {
        title: "4. Ondersteuning door artificiële intelligentie",
        body: [
          "Bepaalde functies (dictaat op de werf, lezen van een kenplaat, voorstel van bestekregels, nalezen van een bestek vóór verzending) berusten op modellen van artificiële intelligentie. Hun resultaten zijn voorstellen: u dient ze te controleren en te valideren vóór verzending naar een klant.",
          "Devisto kan niet aansprakelijk worden gesteld voor een fout in hoeveelheid, prijs, btw-tarief of omschrijving die voortkomt uit een automatische suggestie die u hebt gevalideerd.",
        ],
      },
      {
        title: "5. Beschikbaarheid en aansprakelijkheid",
        body: [
          "Devisto stelt redelijke middelen in om de beschikbaarheid van de dienst te waarborgen. De offlinemodus laat toe zonder netwerk op de werf te blijven werken: de gegevens worden gesynchroniseerd zodra de verbinding terug is.",
          "De aansprakelijkheid van Devisto is in elk geval beperkt tot het bedrag van de sommen die in de laatste twaalf maanden effectief voor het abonnement zijn betaald.",
        ],
      },
      {
        title: "6. Gebruik van de dienst",
        body: [
          "Elke vennootschap beschikt over een beheerdersaccount en kan haar werknemers uitnodigen. De inloggegevens zijn persoonlijk en mogen niet tussen meerdere vennootschappen worden gedeeld. U verbindt zich ertoe correcte informatie te verstrekken en de dienst niet voor frauduleuze doeleinden te gebruiken.",
        ],
      },
      {
        title: "7. Toepasselijk recht",
        body: ["Deze voorwaarden zijn onderworpen aan het Belgisch recht. Elk geschil valt onder de bevoegde rechtbanken van België."],
      },
    ],
  },
};

export default function TermsPage() {
  const { t, lang } = useLanguage();
  const c = content[lang] || content.fr;

  useEffect(() => {
    document.title = `${c.title} | Devisto`;
  }, [c.title]);

  return (
    <div data-testid="terms-page" className="font-body bg-[#F6F8FD] text-[#0F2645] min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-16 sm:py-24">
          <Reveal>
            <p className="text-sm font-bold tracking-[0.1em] uppercase text-[#3A5CC8]">{t("Informations légales")}</p>
            <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F2645]">{c.title}</h1>
            <p className="mt-3 text-xs text-[#5C6A87]">{c.updated}</p>
          </Reveal>
          {c.sections.map((s) => (
            <Section key={s.title} title={s.title}>
              {s.body.map((p, i) => <p key={i}>{p}</p>)}
            </Section>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
